/**
 * Registry and page builder for 12 rashi deep-dive topics (3.2–3.13).
 */
import {
  RASHI_CHART_ROW_ENGLISH,
  RASHI_CHART_ROW_LATIN,
  RASHI_CHART_ROWS,
} from './jyotishaRashiChart';
import { RASHI_SYMBOL_SLUGS_ORDERED } from './jyotishaRashiSymbolImages';
import { KALAPURUSHA_SIGNS } from './jyotishaMyReadingsKalapurushaData';
import { GRAHA_DIGNITY_ROWS } from './jyotishaUcchNeechCalculator';
import { grahaHi } from './jyotishaLagnaWiseAnalysis';
import { nakshatrasInSign } from './jyotishaRashiDeepDiveNakshatras';
import { RASHI_DEEP_DIVE_EXTENSIONS } from './jyotishaRashiDeepDiveExtensions';

const SWABHAV_EN = {
  चर: 'Movable (Chara)',
  स्थिर: 'Fixed (Sthira)',
  द्विस्वभाव: 'Dual (Dwiswabhava)',
};

const GENDER_HI = { विषम: 'पुरुष', सम: 'स्त्री' };
const GENDER_EN = { विषम: 'Male', सम: 'Female' };

const PLANET_ORDER = ['Sun', 'Moon', 'Mars', 'Mercury', 'Jupiter', 'Venus', 'Saturn', 'Rahu', 'Ketu'];

/** @type {{ id: string; num: number; slug: string; syllabus: string }[]} */
export const RASHI_DEEP_DIVE_TOPICS = RASHI_SYMBOL_SLUGS_ORDERED.map((slug, i) => {
  const num = i + 1;
  const hi = RASHI_CHART_ROWS[i].rashi;
  const en = RASHI_CHART_ROW_ENGLISH[i];
  const latin = RASHI_CHART_ROW_LATIN[i];
  return {
    id: `rashi-${slug}`,
    num,
    slug,
    syllabus: `3.${num + 2}`,
    hi,
    en,
    latin,
    tabHi: `${hi} · ${latin}`,
    tabEn: `${latin} · ${en}`,
  };
});

export const RASHI_DEEP_DIVE_TOPIC_IDS = RASHI_DEEP_DIVE_TOPICS.map((t) => t.id);

const TOPIC_BY_ID = Object.fromEntries(RASHI_DEEP_DIVE_TOPICS.map((t) => [t.id, t]));

function dignityPlanetEn(key) {
  return key.charAt(0).toUpperCase() + key.slice(1);
}

function dignityInSign(signNum) {
  const exalted = [];
  const debilitated = [];
  for (const [planetKey, row] of Object.entries(GRAHA_DIGNITY_ROWS)) {
    const planetEn = dignityPlanetEn(planetKey);
    if (row.ucchSign === signNum) {
      exalted.push({ planetEn, planetHi: grahaHi(planetEn), degree: row.paramUcchDegree });
    }
    if (row.neechSign === signNum) {
      debilitated.push({ planetEn, planetHi: grahaHi(planetEn), degree: row.paramNeechDegree });
    }
  }
  return { exalted, debilitated };
}

/**
 * @param {string} topicId
 */
export function getRashiDeepDiveTopic(topicId) {
  return TOPIC_BY_ID[topicId] ?? null;
}

/**
 * @param {string} topicId
 */
export function buildRashiDeepDivePage(topicId) {
  const topic = getRashiDeepDiveTopic(topicId);
  if (!topic) return null;

  const num = topic.num;
  const row = RASHI_CHART_ROWS[num - 1];
  const ext = RASHI_DEEP_DIVE_EXTENSIONS[num];
  const kalapurush = KALAPURUSHA_SIGNS.find((s) => s.num === num);
  const dignity = dignityInSign(num);
  const nakshatras = nakshatrasInSign(num);

  const facts = [
    { labelHi: 'राशि क्रमांक', labelEn: 'Rashi number', value: String(num) },
    { labelHi: 'नाम', labelEn: 'Name', valueHi: row.rashi, valueEn: topic.latin },
    { labelHi: 'अंग्रेज़ी', labelEn: 'English', value: topic.en },
    { labelHi: 'स्वामी', labelEn: 'Lord', valueHi: row.swami, valueEn: row.swami === 'चंद्रमा' ? 'Moon' : row.swami === 'मंगल' ? 'Mars' : row.swami === 'शुक्र' ? 'Venus' : row.swami === 'बुध' ? 'Mercury' : row.swami === 'सूर्य' ? 'Sun' : row.swami === 'गुरु' ? 'Jupiter' : row.swami === 'शनि' ? 'Saturn' : row.swami },
    { labelHi: 'तत्व', labelEn: 'Element', valueHi: row.tatva, valueEn: row.tatva === 'अग्नि' ? 'Fire' : row.tatva === 'पृथ्वी' ? 'Earth' : row.tatva === 'वायु' ? 'Air' : 'Water' },
    { labelHi: 'स्वभाव', labelEn: 'Nature', valueHi: row.swabhav, valueEn: SWABHAV_EN[row.swabhav] ?? row.swabhav },
    { labelHi: 'लिंग', labelEn: 'Gender', valueHi: GENDER_HI[row.samVisham] ?? row.samVisham, valueEn: GENDER_EN[row.samVisham] ?? row.samVisham },
    { labelHi: 'दिशा', labelEn: 'Direction', valueHi: row.disha, valueEn: row.disha === 'पूर्व' ? 'East' : row.disha === 'दक्षिण' ? 'South' : row.disha === 'पश्चिम' ? 'West' : 'North' },
    { labelHi: 'गुण', labelEn: 'Guna', valueHi: row.gun, valueEn: row.gun === 'सात्विक' ? 'Sattva' : row.gun === 'राजसिक' ? 'Rajas' : 'Tamas' },
    { labelHi: 'वर्ण', labelEn: 'Varna', valueHi: row.varn, valueEn: row.varn === 'क्षत्रीय' ? 'Kshatriya' : row.varn === 'वैश्य' ? 'Vaishya' : row.varn === 'शूद्र' ? 'Shudra' : 'Brahmin' },
    { labelHi: 'प्रतीक', labelEn: 'Symbol', valueHi: row.aakriti, valueEn: row.aakriti },
    { labelHi: 'रंग', labelEn: 'Colour', valueHi: row.rang, valueEn: row.rang },
    { labelHi: 'दोष', labelEn: 'Dosha', valueHi: row.prakriti, valueEn: row.prakriti },
  ];

  const planetNotes = PLANET_ORDER.map((planet) => ({
    planetEn: planet,
    planetHi: grahaHi(planet),
    note: ext.planets[planet],
  }));

  return {
    topic,
    row,
    ext,
    kalapurush,
    dignity,
    nakshatras,
    facts,
    planetNotes,
    sections: [
      { id: 'intro', num: 1, titleHi: 'परिचय', titleEn: 'Introduction' },
      { id: 'facts', num: 2, titleHi: 'मूल तथ्य', titleEn: 'Basic facts' },
      { id: 'symbolism', num: 3, titleHi: 'प्रतीक चित्रण', titleEn: 'Symbolism' },
      { id: 'mythology', num: 4, titleHi: 'पौराणिक संबंध', titleEn: 'Mythological connections' },
      { id: 'element', num: 5, titleHi: 'तत्व', titleEn: 'Element (Tattva)' },
      { id: 'swabhav', num: 6, titleHi: 'स्वभाव', titleEn: 'Nature (Swabhav)' },
      { id: 'lord', num: 7, titleHi: 'राशि स्वामी', titleEn: 'Sign lord' },
      { id: 'appearance', num: 8, titleHi: 'शारीरिक प्रवृत्ति', titleEn: 'Physical appearance' },
      { id: 'psych', num: 9, titleHi: 'मनोवैज्ञानिक लक्षण', titleEn: 'Psychological traits' },
      { id: 'kalapurush', num: 10, titleHi: 'कालपुरुष में', titleEn: 'In Kalapurusha' },
      { id: 'purusharthas', num: 11, titleHi: 'पुरुषार्थ', titleEn: 'Purusharthas' },
      { id: 'relationships', num: 12, titleHi: 'संबंध', titleEn: 'Relationships' },
      { id: 'planets', num: 13, titleHi: 'ग्रह इस राशि में', titleEn: 'Planets in this sign' },
      { id: 'dignity', num: 14, titleHi: 'उच्च और नीच', titleEn: 'Exaltation & debilitation' },
      { id: 'nakshatras', num: 15, titleHi: 'नक्षत्र', titleEn: 'Nakshatras inside' },
      { id: 'life', num: 16, titleHi: 'जीवन में', titleEn: 'Through life' },
      { id: 'lagna-moon', num: 17, titleHi: 'लग्न बनाम चंद्र', titleEn: 'Lagna vs Moon vs Sun' },
      { id: 'archetypes', num: 18, titleHi: 'प्रतीकात्मक आकृतियाँ', titleEn: 'Archetypes' },
    ],
  };
}

/**
 * @param {number} num 1–12
 */
export function rashiDeepDiveTopicIdFromNum(num) {
  const t = RASHI_DEEP_DIVE_TOPICS[num - 1];
  return t?.id ?? null;
}
